/** Probate 175 — Stream F */
import DocumentUploadSection from '../../settlement/DocumentUploadSection';
import {
  probateFieldLabelClass,
  probateInputClass,
  ProbateBackLink,
  ProbateChecklistCard,
  ProbateChecklistProgress,
  ProbateContextBlock,
} from './probateStepUi';
import { useProbateDocuments } from './useProbateDocuments';
import { useProbateStep } from './useProbateStep';

const STEP_ID = 'prob-5';
const FORM_FIELDS = 'publicationOutlet,startDate,endDate,creditorClaimDeadline';

export default function SFrame175() {
  const idsExec = ['prob-5-item-1', 'prob-5-item-2', 'prob-5-item-3'];
  const idsComp = ['prob-5-item-4', 'prob-5-item-5', 'prob-5-item-6'];

  const {
    estateId,
    isArchived,
    error,
    stepDef,
    g,
    checklistDefs,
    checklistCompleted,
    checklistTotal,
    checkedIds,
    toggleItem,
    formValues,
    setField,
    touchActivity,
  } = useProbateStep(STEP_ID, FORM_FIELDS);

  const pick = (ids: string[]) => checklistDefs.filter((c) => ids.includes(c.id));

  const ro = !!isArchived;
  const { documents, upload, remove } = useProbateDocuments(estateId, STEP_ID, ro);

  return (
    <div className="max-w-2xl mx-auto py-8 px-4">
      <ProbateBackLink estateId={estateId} />
      {error ? (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">{error}</div>
      ) : null}
      <h1 className="text-2xl font-semibold text-stone-800 mb-2">{stepDef?.title}</h1>
      {g?.point && g?.goal ? <ProbateContextBlock point={g.point} goal={g.goal} /> : null}
      <ProbateChecklistProgress completed={checklistCompleted} total={checklistTotal} />

      <h2 className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-2">Publication setup</h2>
      <div className="bg-white rounded-xl border border-stone-200 p-5 mb-6 space-y-4">
        <h3 className="text-sm font-semibold text-stone-800">Inputs</h3>
        <div>
          <label className={probateFieldLabelClass} htmlFor="publicationOutlet">Outlet name</label>
          <input id="publicationOutlet" className={probateInputClass} disabled={ro}
            value={formValues.publicationOutlet ?? ''} onChange={(e) => setField('publicationOutlet', e.target.value)} />
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className={probateFieldLabelClass} htmlFor="startDate">Publication start</label>
            <input id="startDate" type="date" className={probateInputClass} disabled={ro}
              value={formValues.startDate ?? ''} onChange={(e) => setField('startDate', e.target.value)} />
          </div>
          <div>
            <label className={probateFieldLabelClass} htmlFor="endDate">Required end date</label>
            <input id="endDate" type="date" className={probateInputClass} disabled={ro}
              value={formValues.endDate ?? ''} onChange={(e) => setField('endDate', e.target.value)} />
          </div>
        </div>
      </div>

      <h2 className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-2">Execution</h2>
      <ProbateChecklistCard items={pick(idsExec)} checkedIds={checkedIds} toggleItem={toggleItem} disabled={ro} />

      <h2 className="text-xs font-semibold text-stone-500 uppercase tracking-wider mt-6 mb-2">Compliance</h2>
      <div className="bg-white rounded-xl border border-stone-200 p-5 mb-4 space-y-4">
        <h3 className="text-sm font-semibold text-stone-800 uppercase tracking-wide">Inputs</h3>
        <div>
          <label className={probateFieldLabelClass} htmlFor="creditorClaimDeadline">Creditor claim deadline</label>
          <input id="creditorClaimDeadline" type="date" className={probateInputClass} disabled={ro}
            value={formValues.creditorClaimDeadline ?? ''}
            onChange={(e) => setField('creditorClaimDeadline', e.target.value)} />
          <p className="text-xs text-stone-500 mt-1">Log the statutory or court-ordered cutoff from publication or clerk guidance.</p>
        </div>
      </div>
      <ProbateChecklistCard items={pick(idsComp)} checkedIds={checkedIds} toggleItem={toggleItem} disabled={ro} />

      <div className="bg-white rounded-xl border border-stone-200 p-5 mt-4">
        <DocumentUploadSection
          documents={documents}
          onUpload={async (f) => { await touchActivity(); await upload(f); }}
          onDelete={ro ? undefined : (id) => remove(id)}
          disabled={ro}
        />
        <p className="text-xs text-stone-500 mt-3">Upload affidavit of publication, invoices, proofs, or publisher correspondence.</p>
      </div>
    </div>
  );
}
